import React, { useState, useEffect } from 'react';
import { 
  Shield, 
  Download, 
  RefreshCw, 
  AlertTriangle, 
  CheckCircle, 
  Copy,
  Eye,
  EyeOff,
  Smartphone
} from 'lucide-react';
import { API_ENDPOINTS } from '../config/api';
import { apiService } from '../services/api';

interface MFASecret {
  secret: string;
  qrCodeUrl: string;
  backupCodes: string[];
}

interface MFAStats {
  totalAttempts: number;
  successfulAttempts: number;
  lastSuccessfulLogin: Date | null;
  backupCodesRemaining: number;
}

interface MFAStatus {
  enabled: boolean;
  stats: MFAStats;
}

const MFASettings: React.FC = () => {
  const [mfaStatus, setMfaStatus] = useState<MFAStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [setupStep, setSetupStep] = useState<'disabled' | 'generating' | 'verify' | 'backup'>('disabled');
  const [mfaSecret, setMfaSecret] = useState<MFASecret | null>(null);
  const [totpCode, setTotpCode] = useState('');
  const [password, setPassword] = useState('');
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [newBackupCodes, setNewBackupCodes] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    loadMFAStatus();
  }, []);

  const loadMFAStatus = async () => {
    try {
      setLoading(true);
      const response = await apiService.get<MFAStatus>(API_ENDPOINTS.MFA_STATUS);
      setMfaStatus(response.data);
    } catch (error) {
      console.error('Erreur chargement statut MFA:', error);
      setError('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const generateSecret = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiService.post<MFASecret>(API_ENDPOINTS.MFA_GENERATE_SECRET, {});
      setMfaSecret(response.data);
      setSetupStep('verify');
    } catch (error) {
      console.error('Erreur génération secret:', error);
      setError('Erreur lors de la génération du secret');
    } finally {
      setLoading(false);
    }
  };

  const enableMFA = async () => {
    if (!mfaSecret || !totpCode) {
      setError('Code TOTP requis');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      console.log('🔧 [DEBUG] Activation MFA...');
      console.log('🔧 [DEBUG] Secret length:', mfaSecret.secret.length);
      console.log('🔧 [DEBUG] TOTP code:', totpCode);
      console.log('🔧 [DEBUG] Backup codes count:', mfaSecret.backupCodes.length);
      console.log('🔧 [DEBUG] Token exists:', !!localStorage.getItem('accessToken'));

      const requestBody = {
        secret: mfaSecret.secret,
        totpCode,
        backupCodes: mfaSecret.backupCodes
      };

      console.log('🔧 [DEBUG] Request body prepared');

      await apiService.post(API_ENDPOINTS.MFA_ENABLE, requestBody);
      setSuccess('MFA activé avec succès !');
      setSetupStep('backup');
      await loadMFAStatus();
    } catch (error) {
      console.error('🚨 [DEBUG] Exception caught:', error);
      console.error('🚨 [DEBUG] Error name:', (error as any).name);
      console.error('🚨 [DEBUG] Error message:', (error as any).message);
      setError('Erreur lors de l\'activation');
    } finally {
      setLoading(false);
    }
  };

  const disableMFA = async () => {
    if (!totpCode || !password) {
      setError('Code TOTP et mot de passe requis');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await apiService.post(API_ENDPOINTS.MFA_DISABLE, {
        totpCode,
        password
      });
      
      setSuccess('MFA désactivé avec succès');
      setTotpCode('');
      setPassword('');
      await loadMFAStatus();
    } catch (error) {
      console.error('Erreur désactivation MFA:', error);
      setError('Erreur lors de la désactivation');
    } finally {
      setLoading(false);
    }
  };

  const regenerateBackupCodes = async () => {
    if (!totpCode) {
      setError('Code TOTP requis');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await apiService.post<{ backupCodes: string[] }>(API_ENDPOINTS.MFA_REGENERATE_CODES, { totpCode });
      
      setNewBackupCodes(response.data.backupCodes);
      setSuccess('Nouveaux codes de récupération générés');
      setTotpCode('');
      await loadMFAStatus();
    } catch (error) {
      console.error('Erreur régénération codes:', error);
      setError('Erreur lors de la régénération');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setSuccess('Copié dans le presse-papiers');
    });
  };

  const downloadBackupCodes = (codes: string[]) => {
    const content = `Codes de récupération OSINT Dashboard\n\nConservez ces codes en lieu sûr. Chaque code ne peut être utilisé qu'une seule fois.\n\n${codes.join('\n')}\n\nGénérés le: ${new Date().toLocaleString()}`;
    
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'osint-backup-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading && !mfaStatus) {
    return (
      <div className="p-6">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-osint-orange mx-auto"></div>
        <p className="text-center text-gray-400 mt-4">Chargement...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-3">
        <Shield className="w-6 h-6 text-osint-orange" />
        <h3 className="text-lg font-semibold text-white">Authentification à deux facteurs (2FA)</h3>
      </div>

      {error && (
        <div className="bg-red-500/20 border border-red-500 rounded-lg p-4 flex items-center space-x-2">
          <AlertTriangle className="w-5 h-5 text-red-400" />
          <span className="text-red-400">{error}</span>
        </div>
      )}

      {success && (
        <div className="bg-green-500/20 border border-green-500 rounded-lg p-4 flex items-center space-x-2">
          <CheckCircle className="w-5 h-5 text-green-400" />
          <span className="text-green-400">{success}</span>
        </div>
      )}

      {/* Statut MFA */}
      {mfaStatus && (
        <div className="osint-card p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className={`w-3 h-3 rounded-full ${mfaStatus.enabled ? 'bg-green-500' : 'bg-red-500'}`}></div>
              <span className="text-white font-medium">
                {mfaStatus.enabled ? 'Activé' : 'Désactivé'}
              </span>
            </div>
            {mfaStatus.enabled && (
              <div className="text-sm text-gray-400">
                Codes restants: {mfaStatus.stats.backupCodesRemaining}
              </div>
            )}
          </div>

          {mfaStatus.enabled && (
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-400">Tentatives totales:</span>
                <span className="ml-2 text-white">{mfaStatus.stats.totalAttempts}</span>
              </div>
              <div>
                <span className="text-gray-400">Tentatives réussies:</span>
                <span className="ml-2 text-white">{mfaStatus.stats.successfulAttempts}</span>
              </div>
              {mfaStatus.stats.lastSuccessfulLogin && (
                <div className="col-span-2">
                  <span className="text-gray-400">Dernière connexion:</span>
                  <span className="ml-2 text-white">
                    {new Date(mfaStatus.stats.lastSuccessfulLogin).toLocaleString()}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Configuration MFA */}
      {!mfaStatus?.enabled ? (
        <div className="osint-card p-6">
          <h4 className="text-white font-medium mb-4">Activer l'authentification à deux facteurs</h4>
          
          {setupStep === 'disabled' && (
            <div>
              <p className="text-gray-300 mb-4">
                L'authentification à deux facteurs ajoute une couche de sécurité supplémentaire à votre compte.
                Vous aurez besoin d'une application d'authentification comme Google Authenticator ou Authy.
              </p>
              <button
                onClick={() => {
                  setSetupStep('generating');
                  generateSecret();
                }}
                disabled={loading}
                className="flex items-center space-x-2 px-4 py-2 bg-osint-orange hover:bg-orange-600 rounded-lg text-white transition-colors disabled:opacity-50"
              >
                <Smartphone className="w-4 h-4" />
                <span>Commencer la configuration</span>
              </button>
            </div>
          )}

          {setupStep === 'verify' && mfaSecret && (
            <div className="space-y-6">
              <div>
                <h5 className="text-white font-medium mb-3">1. Scannez le QR code</h5>
                <div className="bg-white p-4 rounded-lg inline-block">
                  <img src={mfaSecret.qrCodeUrl} alt="QR Code" className="w-48 h-48" />
                </div>
                <p className="text-gray-300 text-sm mt-2">
                  Scannez ce code avec votre application d'authentification
                </p>
              </div>

              <div>
                <h5 className="text-white font-medium mb-3">2. Entrez le code de vérification</h5>
                <div className="flex space-x-4">
                  <input
                    type="text"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="123456"
                    className="osint-input w-32 text-center font-mono text-lg"
                    maxLength={6}
                  />
                  <button
                    onClick={enableMFA}
                    disabled={loading || totpCode.length !== 6}
                    className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg text-white transition-colors disabled:opacity-50"
                  >
                    Vérifier et activer
                  </button>
                </div>
              </div>
            </div>
          )}

          {setupStep === 'backup' && mfaSecret && (
            <div className="space-y-4">
              <div className="flex items-center space-x-2 text-green-400">
                <CheckCircle className="w-5 h-5" />
                <span>MFA activé avec succès !</span>
              </div>
              
              <div>
                <h5 className="text-white font-medium mb-3">Codes de récupération</h5>
                <p className="text-gray-300 text-sm mb-4">
                  Conservez ces codes en lieu sûr. Ils vous permettront de vous connecter si vous perdez votre téléphone.
                </p>
                
                <div className="bg-osint-dark border border-osint-light-gray rounded-lg p-4">
                  <div className="grid grid-cols-2 gap-2 font-mono text-sm">
                    {mfaSecret.backupCodes.map((code, index) => (
                      <div key={index} className="flex items-center justify-between p-2 hover:bg-osint-light-gray rounded">
                        <span className="text-white">{code}</span>
                        <button
                          onClick={() => copyToClipboard(code)}
                          className="text-gray-400 hover:text-white"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex space-x-3 mt-4">
                  <button
                    onClick={() => downloadBackupCodes(mfaSecret.backupCodes)}
                    className="flex items-center space-x-2 px-4 py-2 bg-osint-orange hover:bg-orange-600 rounded-lg text-white transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    <span>Télécharger</span>
                  </button>
                  <button
                    onClick={() => setSetupStep('disabled')}
                    className="px-4 py-2 bg-osint-light-gray hover:bg-gray-600 rounded-lg text-white transition-colors"
                  >
                    Terminé
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {/* Désactiver MFA */}
          <div className="osint-card p-6">
            <h4 className="text-white font-medium mb-4">Désactiver l'authentification à deux facteurs</h4>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Code TOTP actuel
                  </label>
                  <input
                    type="text"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="123456"
                    className="osint-input w-full font-mono"
                    maxLength={6}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Mot de passe
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Votre mot de passe"
                    className="osint-input w-full"
                  />
                </div>
              </div>
              <button
                onClick={disableMFA}
                disabled={loading || !totpCode || !password}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-white transition-colors disabled:opacity-50"
              >
                Désactiver MFA
              </button>
            </div>
          </div>

          {/* Régénérer codes de récupération */}
          <div className="osint-card p-6">
            <h4 className="text-white font-medium mb-4">Codes de récupération</h4>
            <p className="text-gray-300 text-sm mb-4">
              Vous avez {mfaStatus.stats.backupCodesRemaining} codes de récupération restants.
              Vous pouvez générer de nouveaux codes si nécessaire.
            </p>
            
            {newBackupCodes.length > 0 && (
              <div className="mb-4">
                <div className="bg-osint-dark border border-osint-light-gray rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-white font-medium">Nouveaux codes</span>
                    <button
                      onClick={() => setShowBackupCodes(!showBackupCodes)}
                      className="text-gray-400 hover:text-white"
                    >
                      {showBackupCodes ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {showBackupCodes && (
                    <div className="grid grid-cols-2 gap-2 font-mono text-sm">
                      {newBackupCodes.map((code, index) => (
                        <div key={index} className="flex items-center justify-between p-2 hover:bg-osint-light-gray rounded">
                          <span className="text-white">{code}</span>
                          <button
                            onClick={() => copyToClipboard(code)}
                            className="text-gray-400 hover:text-white"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => downloadBackupCodes(newBackupCodes)}
                  className="flex items-center space-x-2 px-3 py-1 bg-osint-orange hover:bg-orange-600 rounded text-white text-sm transition-colors mt-2"
                >
                  <Download className="w-4 h-4" />
                  <span>Télécharger</span>
                </button>
              </div>
            )}

            <div className="flex space-x-4">
              <input
                type="text"
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="Code TOTP"
                className="osint-input w-32 font-mono"
                maxLength={6}
              />
              <button
                onClick={regenerateBackupCodes}
                disabled={loading || totpCode.length !== 6}
                className="flex items-center space-x-2 px-4 py-2 bg-osint-orange hover:bg-orange-600 rounded-lg text-white transition-colors disabled:opacity-50"
              >
                <RefreshCw className="w-4 h-4" />
                <span>Générer nouveaux codes</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MFASettings;